import { Rect } from 'fabric'
import { useEffect, useRef, useState } from 'react'

// Apply panning logic. Adds a listener for panning with the middle mouse button,
// and allows entering and exiting pan mode.
export function usePan(activeCanvasRef, canvases) {
  const prevMousePosRef = useRef(null)

  const [isPanning, setIsPanning] = useState(false)

  const togglePanMode = () => {
    setIsPanning(!isPanning)
  }

  useEffect(
    function applyPanMode() {
      if (activeCanvasRef.current) {
        Object.values(canvases).forEach((c) => {
          c.selection = !isPanning
          c.skipTargetFind = isPanning
          c.defaultCursor = isPanning ? 'grab' : 'default'
          c.forEachObject((obj) => {
            obj.selectable = !isPanning
            obj.evented = !isPanning
          })
        })
      }
    },
    [activeCanvasRef, canvases, isPanning],
  )

  useEffect(() => {
    // Set up listeners for panning events outside of react for performance reasons.
    const handlePanStart = (clientX, clientY) => {
      prevMousePosRef.current = { x: clientX, y: clientY }
    }

    const handleMousedown = (e) => {
      if (e.button === 1 || isPanning) {
        handlePanStart(e.clientX, e.clientY)
      }
    }

    const handlePanningMouseMove = (e) => {
      // TODO: These need to be on window in case mouse goes off the canvas.
      if (!prevMousePosRef?.current) return

      const dx = (e.clientX - prevMousePosRef.current.x) * 2
      const dy = (e.clientY - prevMousePosRef.current.y) * 2

      activeCanvasRef.current.relativePan({ x: dx, y: dy })
      prevMousePosRef.current = { x: e.clientX, y: e.clientY }
    }

    const handlePanEnd = () => {
      prevMousePosRef.current = null
    }

    window.addEventListener('mousedown', handleMousedown)
    window.addEventListener('mousemove', handlePanningMouseMove)
    window.addEventListener('mouseup', handlePanEnd)

    return () => {
      window.removeEventListener('mousedown', handleMousedown)
      window.removeEventListener('mousemove', handlePanningMouseMove)
      window.removeEventListener('mouseup', handlePanEnd)
    }
  }, [activeCanvasRef, isPanning])

  return { isPanning, togglePanMode }
}

export function useCrop(canvas, activeObject, ignoreHistory, pushHistory) {
  const [cropImage, setCropImage] = useState(null)
  const [cropRect, setCropRect] = useState(null)
  const [overlayRect, setOverlayRect] = useState(null)

  // Begin cropping. Draw crop rectangle at the size of the image,
  // and prevent anything else from being selected.
  function enterCropMode() {
    // Disable history for duration of crop mode.
    ignoreHistory.current = true
    canvas.getObjects().forEach((obj) => (obj.selectable = false))

    const img = activeObject
    setCropImage(img)

    const prevCropX = img.cropX || 0
    const prevCropY = img.cropY || 0
    const prevCropWidth = img.width
    const prevCropHeight = img.height

    const imageElement = img.getElement()
    img.set({
      cropX: 0,
      cropY: 0,
      width: imageElement.naturalWidth,
      height: imageElement.naturalHeight,
      left: img.left - prevCropX,
      top: img.top - prevCropY,
    })

    const cropLeft = img.left + prevCropX * (img.scaleX || 1)
    const cropTop = img.top + prevCropY * (img.scaleY || 1)
    const cropWidth = prevCropWidth * (img.scaleX || 1)
    const cropHeight = prevCropHeight * (img.scaleY || 1)

    let cropGeometry = {
      left: cropLeft,
      top: cropTop,
      width: cropWidth * (img.scaleX || 1),
      height: cropHeight * (img.scaleY || 1),
    }

    const crop = new Rect({
      ...cropGeometry,
      fill: 'rgba(0,0,0,0.15)',
      stroke: 'red',
      strokeDashArray: [5, 5],
      cornerColor: 'red',
      transparentCorners: false,
      lockRotation: true,
      selectable: true,
      evented: true,
    })
    crop.setControlsVisibility({ mtr: false })
    setCropRect(crop)

    canvas.add(crop)

    const canvasWidth = canvas.getWidth()
    const canvasHeight = canvas.getHeight()

    const overlay = new Rect({
      left: 0,
      top: 0,
      width: canvasWidth,
      height: canvasHeight,
      fill: 'rgba(0,0,0,0.45)',
      selectable: false,
      evented: false,
      hoverCursor: 'default',
    })
    setOverlayRect(overlay)

    const hole = new Rect({
      left: crop.left,
      top: crop.top,
      width: crop.width * crop.scaleX,
      height: crop.height * crop.scaleY,
      absolutePositioned: true,
      inverted: true, // <--- the key!
    })

    overlay.clipPath = hole

    canvas.add(overlay)
    canvas.bringObjectToFront(crop) // always keep cropRect above overlayRect

    function updateOverlayClip() {
      if (!overlay || !crop) return
      overlay.clipPath.set({
        left: crop.left,
        top: crop.top,
        width: crop.width * crop.scaleX,
        height: crop.height * crop.scaleY,
        absolutePositioned: true,
      })
      canvas.requestRenderAll()
    }

    const onUpdateCrop = function () {
      // Constrain crop to size of image
      const imgLeft = img.left
      const imgTop = img.top
      const imgWidth = img.width * (img.scaleX || 1)
      const imgHeight = img.height * (img.scaleY || 1)

      const left = crop.left,
        top = crop.top,
        width = crop.width * crop.scaleX,
        height = crop.height * crop.scaleY

      const cropWiderThanImg = crop.width * crop.scaleX > imgWidth
      if (cropWiderThanImg) {
        crop.width = imgWidth
        crop.scaleX = 1

        crop.left = imgLeft
        crop.top = imgTop
      } else {
        if (left < imgLeft) {
          crop.left = imgLeft
        }
        if (left + width > imgLeft + imgWidth) {
          crop.left = imgLeft + imgWidth - width
        }
      }

      const cropTallerThanImg = crop.height * crop.scaleY > imgHeight
      if (cropTallerThanImg) {
        crop.height = imgHeight
        crop.scaleY = 1

        crop.left = imgLeft
        crop.top = imgTop
      } else {
        if (top < imgTop) {
          crop.top = imgTop
        }
        if (top + height > imgTop + imgHeight) {
          crop.top = imgTop + imgHeight - height
        }
      }

      crop.setCoords()
      updateOverlayClip()

      canvas.requestRenderAll()
    }

    // Update overlay hole position when cropRect moves/scales
    crop.on('modified', onUpdateCrop)

    updateOverlayClip()
    canvas.setActiveObject(crop)
    canvas.requestRenderAll()
  }

  // Apply mask to image, and re-enable normal canvas functionality.
  async function applyCrop() {
    // Offset of crop relative to image.
    const cropX = (cropRect.left - cropImage.left) / cropImage.scaleX
    const cropY = (cropRect.top - cropImage.top) / cropImage.scaleY

    // Size of crop
    const width = (cropRect.width * cropRect.scaleX) / cropImage.scaleX
    const height = (cropRect.height * cropRect.scaleY) / cropImage.scaleY

    cropImage.set({
      cropX,
      cropY,
      width,
      height,
      top: cropImage.top + cropY * cropImage.scaleY,
      left: cropImage.left + cropX * cropImage.scaleX,
    })
    cropImage.setCoords()

    canvas.remove(overlayRect)
    canvas.remove(cropRect)
    canvas.getObjects().forEach((obj) => (obj.selectable = true))
    canvas.renderAll()

    ignoreHistory.current = false
    pushHistory(canvas)

    setOverlayRect(null)
    setCropRect(null)
    setCropImage(null)
  }

  return { cropImage, enterCropMode, applyCrop }
}
